/**
 * UnlinkUseCaseDialog
 *
 * Shared confirmation dialog for removing a use case link from an application.
 * Used by:
 *   - ConnectionsAiTab (/connections/ai) — use case owner perspective
 *   - ApplicationAiResources — application owner perspective
 *
 * Owns the RemoveUseCaseFromApplication mutation internally so callers only
 * need to manage open/close state.
 */
import { useState } from 'react';
import { useMutation } from '@apollo/client';
import {
  DevHubAlert,
  DevHubBox,
  DevHubButton,
  DevHubCircularProgress,
  DevHubDialog,
  DevHubDialogActions,
  DevHubDialogContent,
  DevHubDialogTitle,
  DevHubGrid,
  DevHubIconButton,
  DevHubTypography,
} from '@devxdiscover/devhub-ui';
import CloseIcon from '@material-ui/icons/Close';
import { RemoveUseCaseFromApplicationMutation } from './queries';

interface Props {
  useCaseId: string;
  applicationId: number;
  applicationName?: string;
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

/**
 * Dialog to confirm unlinking an AI use case from an application.
 * @param root0 - Component props
 */
export const UnlinkUseCaseDialog = ({
  useCaseId,
  applicationId,
  applicationName,
  open,
  onClose,
  onSuccess,
}: Props) => {
  const [isUnlinking, setIsUnlinking] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [removeUseCaseFromApplication] = useMutation(
    RemoveUseCaseFromApplicationMutation,
  );

  const handleClose = () => {
    setError(null);
    onClose();
  };

  const handleUnlink = async () => {
    setError(null);
    setIsUnlinking(true);
    try {
      const { data } = await removeUseCaseFromApplication({
        variables: { input: { applicationId, useCaseId } },
      });
      const result = data?.resources?.application?.removeUseCaseFromApplication;
      if (result?.success) {
        handleClose();
        onSuccess();
      } else {
        setError(result?.message ?? 'Unlink failed. Please try again.');
      }
    } catch (e: any) {
      setError(e?.message ?? 'Unlink failed. Please try again.');
    } finally {
      setIsUnlinking(false);
    }
  };

  const appLabel = applicationName ?? 'this application';

  return (
    <DevHubDialog
      open={open}
      onClose={handleClose}
      maxWidth="xs"
      PaperProps={{ style: { width: '400px' } }}
    >
      <DevHubDialogTitle>
        <DevHubGrid
          container
          justifyContent="space-between"
          alignItems="center"
        >
          <DevHubTypography variant="h5" weight="light" fontColor="purpleBlue">
            Unlink use case
          </DevHubTypography>
          <DevHubIconButton onClick={handleClose}>
            <CloseIcon />
          </DevHubIconButton>
        </DevHubGrid>
      </DevHubDialogTitle>
      <DevHubDialogContent>
        <DevHubBox padding="medium">
          <DevHubTypography variant="body2">
            Remove <strong>{useCaseId}</strong> from <strong>{appLabel}</strong>
            ?
          </DevHubTypography>
          <DevHubTypography
            variant="body2"
            opacity="hazy"
            style={{ marginTop: '8px' }}
          >
            The use case will become unlinked and can be re-linked to another
            application.
          </DevHubTypography>
          {error && (
            <DevHubAlert severity="error" style={{ marginTop: '12px' }}>
              {error}
            </DevHubAlert>
          )}
        </DevHubBox>
      </DevHubDialogContent>
      <DevHubDialogActions
        style={{ padding: '16px 24px', justifyContent: 'space-between' }}
      >
        <DevHubButton
          variant="outlined"
          onClick={handleClose}
          disabled={isUnlinking}
        >
          Cancel
        </DevHubButton>
        <DevHubButton
          variant="contained"
          color="primary"
          onClick={handleUnlink}
          disabled={isUnlinking}
        >
          {isUnlinking ? <DevHubCircularProgress size={20} /> : 'Unlink'}
        </DevHubButton>
      </DevHubDialogActions>
    </DevHubDialog>
  );
};
